import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { BehaviorSubject, from, Observable, of } from 'rxjs'
import { deserializeArray, selectOnce, toMoment } from '@tokeet/tv3-platform'
import * as fromRoot from '../state'
import { Store } from '@ngrx/store'
import { CalendarResponse } from '@tv3/interfaces/responses/calendar.response'
import { CalendarEvent } from '@tv3/store/calendar/calendar.model'
import { AuthService } from '@tv3/services/auth.service'
import { selectIsDoubleBookingDisabled } from '@tv3/store/preferences/preferences.selectors'
import { concatMap, flatMap, map, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import * as moment from 'moment'
import { HoldResponse } from '@tv3/interfaces/responses/hold.response'
import { HoldRequests } from '@tv3/interfaces/requests/hold-requests'
import { HoldRequest } from '@tv3/interfaces/requests/hold-request'
import { CalendarEventView } from './calendar.view'

@Injectable({
  providedIn: 'root',
})
export class CalendarService {
  constructor(private http: HttpClient, private auth: AuthService, private store: Store<fromRoot.State>) {}

  allHolds(rentalId?: string): Observable<CalendarEvent[]> {
    const url = `@api/calendar/holds/all/${rentalId ? `rental/${rentalId}` : ''}`

    return this.http.get<HoldResponse[]>(url).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  create(requests: HoldRequests): Observable<CalendarEvent[]> {
    const url = '@api/calendar/'

    requests = this.adjustHoldTime(requests) as HoldRequests

    return of(requests).pipe(
      flatMap((request) => from(request.rentals)),
      map(
        (rentalId) =>
          ({
            title: requests.title,
            type: requests.type,
            rental_id: rentalId,
            start: requests.start,
            end: requests.end,
            source: requests.source,
            rolling: requests.rolling || 0,
            expires: requests.expires,
            color: requests.color,
          } as HoldRequest)
      ),
      concatMap((request) => this.http.post<object>(url, request).pipe(map((hold) => CalendarEvent.deserialize(hold)))),
      toArray()
    )
  }

  update(payload: { request: HoldRequest; hold: CalendarEvent }): Observable<CalendarEvent> {
    const url = `@api/calendar/update/${payload.hold.id}`

    return this.http.put(url, this.adjustHoldTime(payload.request)).pipe(map((hold) => CalendarEvent.deserialize(hold)))
  }

  adjustHoldTime(hold: HoldRequest | HoldRequests) {
    const start = toMoment(hold.start).startOf('date').add(11, 'hours').unix()
    const end = toMoment(hold.end).startOf('date').add(11, 'hours').unix()
    const expires = toMoment(hold.expires).startOf('date').unix()

    return { ...hold, start, end, expires }
  }

  delete(id: string) {
    const url = `@api/calendar/delete/${id}`

    return this.http.delete(url)
  }

  deleteBatch(ids: string[]): Observable<string[]> {
    const url = '@api/calendar/delete/batch'

    return this.http.put<string[]>(url, { ids })
  }

  byRental(rentalId: string): Observable<CalendarEvent[]> {
    const url = `@api/calendar/all/${rentalId}`

    return this.http.get<CalendarResponse[]>(url).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  byRange(request: { start: number; end: number; past?: number }): Observable<CalendarEvent[]> {
    let past = request.past

    if (R.isNil(past)) {
      const now = moment.utc().endOf('date').unix()

      // if start is less than 1 year ago, so increase past
      if (now - request.start > 31104000) {
        past = now - request.start
      }
    }

    const params: Record<string, string> = {
      start: request.start.toString(),
      end: request.end.toString(),
    }

    if (!R.isNil(past)) {
      params.past = past.toString()
    }

    const url = '@api/calendar/all/'

    return this.http.get<CalendarResponse[]>(url, { params }).pipe(deserializeArray<CalendarEvent>(CalendarEvent))
  }

  checkOverlapping(range: { start; end }, events: CalendarEvent[], isBooking = true): any {
    const result = new BehaviorSubject<any>(null)

    this.store
      .pipe(
        selectOnce(selectIsDoubleBookingDisabled),
        map((doubleBookingDisabled) => {
          const eventStart = R.is(Number, range.start) ? moment.utc(range.start * 1000) : moment.utc(range.start)
          const eventEnd = R.is(Number, range.end) ? moment.utc(range.end * 1000) : moment.utc(range.end)
          if (!eventStart.isSame(eventEnd, 'day')) {
            eventEnd.subtract(1, 'days')
          }

          let overlappingEvent = null

          for (let i = 0, len = events.length; i < len; i++) {
            const event = events[i]
            if (isBooking && !!event.bookingId && !doubleBookingDisabled) {
              // two bookings can overlap
              continue
            }
            const start = R.is(Number, event.start) ? moment.utc(event.start * 1000) : moment.utc(event.start)
            const end = R.is(Number, event.start) ? moment.utc(event.end * 1000) : moment.utc(event.end)
            if (!start.isSame(end, 'day')) {
              end.subtract(1, 'days')
            }

            if (
              eventStart.isBetween(start, end, 'day') ||
              eventEnd.isBetween(start, end, 'day') ||
              start.isBetween(eventStart, eventEnd) ||
              end.isBetween(eventStart, eventEnd) ||
              start.isSame(eventStart, 'day') ||
              start.isSame(eventEnd, 'day') ||
              end.isSame(eventStart, 'day') ||
              end.isSame(eventEnd, 'day')
            ) {
              overlappingEvent = event
              break
            }
          }
          return overlappingEvent
        })
      )
      .subscribe((overlappingEvent) => result.next(overlappingEvent))

    return result.value
  }

  getEventID(event: CalendarEvent) {
    return event.id || event.bookingId
  }

  getEventTimeRange(event: CalendarEventView) {
    if (!event.isHoldEvent) return { start: event.start, end: event.end, event }

    const start = toMoment(event.start)
    const end = toMoment(event.end)

    // TV3-1124:
    // if a hold event is a single day. the "start" and "date" fields will have the same value.
    // in this case, we add some hours to "end" field to make sure "start" and "end" time are different.
    // so the event will be visible in the calendar.

    if (start.isSame(end, 'date')) {
      end.endOf('date')
    }

    if (start.isSame(end, 'hour')) {
      end.add(6, 'hour')
    }

    return { start: start.unix(), end: end.unix(), event }
  }
}
